<template>
  <div class="calico-ai-reply">
    <img
      src="@/assets/logos/BetaScore-Mark-Full_Color.svg"
      class="icon icon--xsmall"
    >
    <div
      v-if="message"
      class="message-content body--medium markdown-content"
      v-html="safeMessage"
    />
    <CalicoAiResponseLoader
      v-else
      class="message-content"
    />
  </div>
</template>

<script>
import { marked } from 'marked'
import DOMPurify from 'dompurify'
import CalicoAiResponseLoader from '@/components/calico/aiResponseLoader/index.vue'

export default {
  name: 'CalicoAIReply',
  components: {
    CalicoAiResponseLoader
  },
  props: {
    message: {
      type: String,
      default: ''
    }
  },
  computed: {
    safeMessage() {
      const cleaned = this.message
      ?.replace(/\[([i-z, ]*)\]/g, '');
      const rawHtml = marked(cleaned || '');
      return DOMPurify.sanitize(rawHtml);
    }
  }
}
</script>

<style lang="scss" scoped>
.calico-ai-reply {
  @include inline-space($space-small);
  align-items: flex-start;
  color: $color-text-primary;
  max-width: 100%;

  .icon {
    padding: $space-xsmall;
  }

  :deep(.message-content) {
    max-width: 100%;
    min-width: 0;

    p {
      margin: 0;
      padding: 0;
      margin-bottom: $space-small;
      max-width: 100%;

      &:last-child {
        margin-bottom: 0;
      }
    }

    strong {
      font-weight: $font-weight-heavy;
    }

    em, i {
      font-style: italic;
    }

    code {
      background-color: $color-neutral-light;
      padding: $space-xxsmall $space-xsmall;
      border-radius:  $border-width-default;
      font-family: monospace;
      font-size: 0.9em;
    }

    pre {
      background-color: $color-neutral-light;
      padding: $space-small;
      border-radius: $space-xsmall;
      margin: $space-small 0;
      font-family: monospace;
      white-space: pre-wrap;
    }

    table {
      max-width: 100%;
      overflow-x: auto;
      width: 100%;
      border-collapse: collapse;
      margin: $space-small 0 $space-small 0;
      display: block;
      white-space: nowrap;
      scrollbar-width: thin;
      scrollbar-color: $color-neutral-light $color-neutral-light;

      &::-webkit-scrollbar {
        height: 3px;
      }

      &::-webkit-scrollbar-track {
        background: $color-neutral-light;
        border-radius: 4px;
      }

      &::-webkit-scrollbar-thumb {
        background: $color-neutral-light;
        border-radius: 4px;
        
        &:hover {
          background: $color-neutral;
        }
      }
    }

    th,
    td {
      border: 1px solid $color-neutral-light;
      padding: $space-small;
      text-align: left;
    }

    th {
      background-color: $color-neutral-light;
    }

    ul, ol {
      padding-left: $space-medium;
      margin: $space-small 0;

      li {
        margin-bottom: $space-xsmall;
        line-height: 1.5;
      }
    }

    blockquote {
      border-left:  $border-width-default solid $color-neutral-light;
      padding-left: $space-small;
      margin: $space-small 0;
      color: inherit;
      font-style: italic;
    }

    h1, h2, h3, h4, h5, h6 {
      margin: $space-medium 0 $space-small 0;
      font-weight: $font-weight-heavy;
    }

    a {
      text-decoration: underline;
      font-weight: $font-weight-heavy;
      
      &:hover {
        opacity: 0.8;
      }
    }

    img {
      max-width: 100%;
      height: auto;
      border-radius: $space-xsmall;
      margin: $space-small 0;
    }

    hr {
      border: none;
      border-top: 1px solid $color-neutral-light;
      margin: $space-medium 0;
    }

    kbd {
      background-color: $color-neutral-light;
      border-radius:  $border-width-default;
      border: 1px solid $color-neutral-light;
      color: inherit;
      display: inline-block;
      font-weight: $font-weight-heavy;
      line-height: 1;
      padding: $space-xxsmall $space-xsmall;
      white-space: nowrap;
    }

    mark {
      background-color: $color-neutral-light;
      padding: 0 $space-xxsmall;
      border-radius: $space-xxsmall;
    }

    del {
      text-decoration: line-through;
      color: $color-text-secondary;
    }

    ins {
      text-decoration: underline;
      font-weight: $font-weight-heavy;
    }

    .citation {
      font-size: $font-size-xsmall;
      color: $color-text-secondary;
      font-weight: $font-weight-regular;
      vertical-align: super;
    }
  }
}
</style>